import React, { useState, useEffect, useCallback } from 'react';
import { useSelector } from 'react-redux';
import {
    Box, Typography, Paper, Grid, Card, CardContent, CardActions,
    Button, IconButton, Dialog, DialogTitle, DialogContent, DialogActions,
    TextField, Chip, Alert, CircularProgress, Select, MenuItem, FormControl,
    InputLabel, Switch, FormControlLabel, Tooltip, Divider, Tabs, Tab,
    Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
    Accordion, AccordionSummary, AccordionDetails, Badge,
} from '@mui/material';
import {
    Add, Edit, Delete, Send, History, WhatsApp, CheckCircle, Cancel,
    ExpandMore, ContentCopy, Star, StarBorder, Visibility, Close,
    Assignment, CloudUpload, GetApp, People,
} from '@mui/icons-material';
import { whatsappTemplatesAPI, whatsappAPI, companyAPI } from '../../services/api';
import { selectIsSuperAdmin, selectIsCompanyAdmin, selectUser } from '../../store/authSlice';

// ──── helpers ────────────────────────────────────────────────────────────────

/** Extract {{1}}, {{2}} … from a string, return sorted unique indices */
const extractVarIndices = (text = '') => {
    const matches = [...text.matchAll(/\{\{(\d+)\}\}/g)];
    return [...new Set(matches.map(m => parseInt(m[1])))].sort((a, b) => a - b);
};

/** Replace {{1}} → actual value for preview */
const applyVars = (text = '', vars = {}) => {
    if (!text) return '';
    return text.replace(/\{\{(\d+)\}\}/g, (_, i) => vars[i] || `{{${i}}}`);
};

const CATEGORIES = ['MARKETING', 'UTILITY', 'AUTHENTICATION'];
const HEADER_TYPES = ['NONE', 'TEXT', 'IMAGE', 'VIDEO', 'DOCUMENT'];
const PURPOSES = ['custom', 'welcome', 'order_update', 'payment', 'reminder', 'otp'];
const STATUSES = ['active', 'inactive', 'draft'];

// ──── empty form ──────────────────────────────────────────────────────────────
const emptyForm = () => ({
    company_id: '',
    template_id: '',
    template_name: '',
    display_name: '',
    language_code: 'en',
    category: 'UTILITY',
    header_type: 'NONE',
    header_text: '',
    header_variables: [],
    body_text: '',
    body_variables: [],   // [{index, name, label, example}]
    footer_text: '',
    buttons: [],           // [{type, text, url?, phone_number?}]
    status: 'active',
    is_default: false,
    purpose: 'custom',
    notes: '',
});

// ──── Tab Panel helper ────────────────────────────────────────────────────────
function TabPanel({ value, index, children }) {
    return value === index ? <Box sx={{ pt: 2 }}>{children}</Box> : null;
}

// ──── Main Component ──────────────────────────────────────────────────────────
const WhatsAppTemplates = () => {
    const isSuperAdmin = useSelector(selectIsSuperAdmin);
    const isCompanyAdmin = useSelector(selectIsCompanyAdmin);
    const user = useSelector(selectUser);

    const [templates, setTemplates] = useState([]);
    const [companies, setCompanies] = useState([]);
    const [waNumbers, setWaNumbers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');

    // Add / Edit dialog
    const [openDialog, setOpenDialog] = useState(false);
    const [editingTemplate, setEditingTemplate] = useState(null);
    const [formData, setFormData] = useState(emptyForm());
    const [saving, setSaving] = useState(false);
    const [dialogTab, setDialogTab] = useState(0);

    // Send dialog
    const [openSendDialog, setOpenSendDialog] = useState(false);
    const [sendTarget, setSendTarget] = useState(null);
    const [sendForm, setSendForm] = useState({ recipient_phone: '', recipient_name: '', body_variables: {}, header_variables: {}, whatsapp_number_id: '' });
    const [sending, setSending] = useState(false);
    const [uploadingMedia, setUploadingMedia] = useState(false);

    // History dialog
    const [openHistory, setOpenHistory] = useState(false);
    const [historyTarget, setHistoryTarget] = useState(null);
    const [historyData, setHistoryData] = useState([]);
    const [historyLoading, setHistoryLoading] = useState(false);

    // Preview dialog
    const [openPreview, setOpenPreview] = useState(false);
    const [previewTemplate, setPreviewTemplate] = useState(null);

    // Delete confirm
    const [deleteTarget, setDeleteTarget] = useState(null);

    // Bulk send dialog
    const [openBulkDialog, setOpenBulkDialog] = useState(false);
    const [bulkTarget, setBulkTarget] = useState(null);
    const [bulkFile, setBulkFile] = useState(null);
    const [bulkSending, setBulkSending] = useState(false);
    const [bulkResults, setBulkResults] = useState(null);

    // ── data loading ──────────────────────────────────────────────────────────
    const fetchTemplates = useCallback(async () => {
        try {
            setLoading(true);
            const res = await whatsappTemplatesAPI.getAll();
            setTemplates(res.data.data || []);
            setError('');
        } catch (err) {
            setError('Failed to load templates');
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchTemplates();
        if (isSuperAdmin) {
            companyAPI.getAll().then(r => setCompanies(r.data.data || [])).catch(() => {});
        }
        whatsappAPI.getNumbers().then(r => setWaNumbers(r.data.data || [])).catch(() => {});
    }, [fetchTemplates, isSuperAdmin]);

    // ── auto-sync body_variables when body_text changes ───────────────────────
    const syncBodyVars = (bodyText, existingVars = []) => {
        const indices = extractVarIndices(bodyText);
        const existing = existingVars.reduce((acc, v) => { acc[v.index] = v; return acc; }, {});
        return indices.map(i => existing[i] || { index: i, name: '', label: `Variable ${i}`, example: '' });
    };

    // ── form helpers ──────────────────────────────────────────────────────────
    const handleFormChange = (field, value) => {
        setFormData(prev => {
            const updated = { ...prev, [field]: value };
            if (field === 'body_text') {
                updated.body_variables = syncBodyVars(value, prev.body_variables);
            }
            return updated;
        });
    };

    const handleBodyVarChange = (index, key, value) => {
        setFormData(prev => ({
            ...prev,
            body_variables: prev.body_variables.map(v =>
                v.index === index ? { ...v, [key]: value } : v
            ),
        }));
    };

    // Buttons
    const addButton = () =>
        setFormData(prev => ({
            ...prev,
            buttons: [...prev.buttons, { type: 'QUICK_REPLY', text: '' }],
        }));

    const updateButton = (idx, key, value) =>
        setFormData(prev => ({
            ...prev,
            buttons: prev.buttons.map((b, i) => i === idx ? { ...b, [key]: value } : b),
        }));

    const removeButton = (idx) =>
        setFormData(prev => ({ ...prev, buttons: prev.buttons.filter((_, i) => i !== idx) }));

    // ── open dialogs ──────────────────────────────────────────────────────────
    const handleOpenAdd = () => {
        setEditingTemplate(null);
        const form = emptyForm();
        if (!isSuperAdmin) form.company_id = user?.companyId || '';
        setFormData(form);
        setDialogTab(0);
        setOpenDialog(true);
    };

    const handleOpenEdit = (tpl) => {
        setEditingTemplate(tpl);
        setFormData({
            company_id: tpl.company_id || '',
            template_id: tpl.template_id || '',
            template_name: tpl.template_name || '',
            display_name: tpl.display_name || '',
            language_code: tpl.language_code || 'en',
            category: tpl.category || 'UTILITY',
            header_type: tpl.header_type || 'NONE',
            header_text: tpl.header_text || '',
            header_variables: tpl.header_variables
                ? (typeof tpl.header_variables === 'string' ? JSON.parse(tpl.header_variables) : tpl.header_variables)
                : [],
            body_text: tpl.body_text || '',
            body_variables: tpl.body_variables
                ? (typeof tpl.body_variables === 'string' ? JSON.parse(tpl.body_variables) : tpl.body_variables)
                : [],
            footer_text: tpl.footer_text || '',
            buttons: tpl.buttons
                ? (typeof tpl.buttons === 'string' ? JSON.parse(tpl.buttons) : tpl.buttons)
                : [],
            status: tpl.status || 'active',
            is_default: Boolean(tpl.is_default),
            purpose: tpl.purpose || 'custom',
            notes: tpl.notes || '',
        });
        setDialogTab(0);
        setOpenDialog(true);
    };

    const handleOpenSend = (tpl) => {
        setSendTarget(tpl);
        const bodyVars = tpl.body_variables
            ? (typeof tpl.body_variables === 'string' ? JSON.parse(tpl.body_variables) : tpl.body_variables)
            : [];
        const initVars = {};
        bodyVars.forEach(v => { initVars[v.index] = ''; });
        setSendForm({ recipient_phone: '', recipient_name: '', body_variables: initVars, header_variables: {}, whatsapp_number_id: waNumbers[0]?.id || '' });
        setOpenSendDialog(true);
    };

    const handleOpenHistory = async (tpl) => {
        setHistoryTarget(tpl);
        setOpenHistory(true);
        setHistoryLoading(true);
        try {
            const res = await whatsappTemplatesAPI.getSends(tpl.id);
            setHistoryData(res.data.data || []);
        } catch {
            setHistoryData([]);
        } finally {
            setHistoryLoading(false);
        }
    };

    // ── save template ─────────────────────────────────────────────────────────
    const handleSave = async () => {
        try {
            setSaving(true);
            setError('');
            const payload = {
                ...formData,
                body_variables: formData.body_variables.length ? formData.body_variables : null,
                buttons: formData.buttons.length ? formData.buttons : null,
                header_variables: formData.header_variables?.length ? formData.header_variables : null,
            };
            if (editingTemplate) {
                await whatsappTemplatesAPI.update(editingTemplate.id, payload);
            } else {
                await whatsappTemplatesAPI.create(payload);
            }
            setSuccess(editingTemplate ? 'Template updated!' : 'Template created!');
            setOpenDialog(false);
            fetchTemplates();
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to save template');
        } finally {
            setSaving(false);
        }
    };

    // ── delete template ───────────────────────────────────────────────────────
    const handleDelete = async () => {
        if (!deleteTarget) return;
        try {
            await whatsappTemplatesAPI.delete(deleteTarget.id);
            setSuccess('Template deleted');
            setDeleteTarget(null);
            fetchTemplates();
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to delete template');
        }
    };

    // ── upload template media ──────────────────────────────────────────────────
    const handleMediaUpload = async (event) => {
        const file = event.target.files?.[0];
        if (!file) return;

        try {
            setUploadingMedia(true);
            setError('');

            const formData = new FormData();
            formData.append('media', file);

            const response = await whatsappTemplatesAPI.uploadMedia(formData);
            
            // Set the uploaded file URL in the header_variables
            setSendForm(f => ({
                ...f,
                header_variables: { ...f.header_variables, url: response.data.data.url }
            }));

            setSuccess('File uploaded successfully!');
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to upload file');
        } finally {
            setUploadingMedia(false);
        }
    };

    // ── send template ─────────────────────────────────────────────────────────
    const handleSend = async () => {
        if (!sendTarget) return;

        // Validate header media URL if required
        if (['IMAGE', 'VIDEO', 'DOCUMENT'].includes(sendTarget.header_type) && !sendForm.header_variables?.url) {
            setError(`Please provide a ${sendTarget.header_type.toLowerCase()} URL`);
            return;
        }

        try {
            setSending(true);
            setError('');
            await whatsappTemplatesAPI.send(sendTarget.id, sendForm);
            setSuccess(`Template sent to ${sendForm.recipient_phone}!`);
            setOpenSendDialog(false);
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to send template');
        } finally {
            setSending(false);
        }
    };

    // ── bulk send handlers ────────────────────────────────────────────────────
    const handleOpenBulkSend = (tpl) => {
        setBulkTarget(tpl);
        setBulkFile(null);
        setBulkResults(null);
        setOpenBulkDialog(true);
    };

    const handleBulkFileChange = (event) => {
        const file = event.target.files?.[0];
        if (file) {
            setBulkFile(file);
            setError('');
        }
    };

    const handleBulkSend = async () => {
        if (!bulkTarget || !bulkFile) {
            setError('Please select an Excel file');
            return;
        }

        try {
            setBulkSending(true);
            setError('');
            setBulkResults(null);

            const formData = new FormData();
            formData.append('excel_file', bulkFile);
            if (waNumbers[0]?.id) {
                formData.append('whatsapp_number_id', waNumbers[0].id);
            }

            const response = await whatsappTemplatesAPI.bulkSend(bulkTarget.id, formData);
            
            setBulkResults(response.data.data);
            setSuccess(response.data.message);
            
            // Refresh send history if needed
            fetchTemplates();
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to send bulk messages');
        } finally {
            setBulkSending(false);
        }
    };

    const downloadSampleExcel = () => {
        // Create sample Excel data based on template variables
        const bodyVars = bulkTarget?.body_variables
            ? (typeof bulkTarget.body_variables === 'string' ? JSON.parse(bulkTarget.body_variables) : bulkTarget.body_variables)
            : [];

        const headers = ['phone', 'name'];
        bodyVars.forEach(v => {
            headers.push(v.name || `var${v.index}`);
        });
        
        if (bulkTarget?.header_type === 'TEXT') {
            headers.push('header_text');
        } else if (['IMAGE', 'VIDEO', 'DOCUMENT'].includes(bulkTarget?.header_type)) {
            headers.push('media_url');
        }

        // Create sample row
        const sampleRow = {
            phone: '+923001234567',
            name: 'Ahmad Ali'
        };
        bodyVars.forEach(v => {
            sampleRow[v.name || `var${v.index}`] = v.example || `Sample ${v.label}`;
        });
        if (bulkTarget?.header_type === 'TEXT') {
            sampleRow.header_text = 'Sample Header';
        } else if (['IMAGE', 'VIDEO', 'DOCUMENT'].includes(bulkTarget?.header_type)) {
            sampleRow.media_url = 'https://example.com/image.jpg';
        }

        // Create CSV content
        const csvContent = [
            headers.join(','),
            headers.map(h => sampleRow[h] || '').join(',')
        ].join('\n');

        // Download
        const blob = new Blob([csvContent], { type: 'text/csv' });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `bulk_send_template_${bulkTarget?.template_name || 'sample'}.csv`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        window.URL.revokeObjectURL(url);
    };

    // ── template card preview text ────────────────────────────────────────────
    const previewBody = (tpl) => {
        const bodyVars = tpl.body_variables
            ? (typeof tpl.body_variables === 'string' ? JSON.parse(tpl.body_variables) : tpl.body_variables)
            : [];
        const exampleVars = bodyVars.reduce((acc, v) => { acc[v.index] = v.example || `[${v.label}]`; return acc; }, {});
        return applyVars(tpl.body_text, exampleVars);
    };

    // ── render ────────────────────────────────────────────────────────────────
    return (
        <Box sx={{ p: 3 }}>
            {/* Header */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                <Box>
                    <Typography variant="h5" fontWeight={700} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <WhatsApp sx={{ color: '#25D366' }} />
                        WhatsApp Templates
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        Manage Meta-approved message templates — add, edit, send, and track history.
                    </Typography>
                </Box>
                {(isSuperAdmin || isCompanyAdmin) && (
                    <Button variant="contained" startIcon={<Add />} onClick={handleOpenAdd}>
                        Add Template
                    </Button>
                )}
            </Box>

            {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}
            {success && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>{success}</Alert>}

            {loading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
                    <CircularProgress />
                </Box>
            ) : templates.length === 0 ? (
                <Paper sx={{ p: 6, textAlign: 'center' }}>
                    <Assignment sx={{ fontSize: 64, color: 'text.disabled', mb: 2 }} />
                    <Typography variant="h6" color="text.secondary">No templates yet</Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                        Add your first Meta-approved WhatsApp template to get started.
                    </Typography>
                    {(isSuperAdmin || isCompanyAdmin) && (
                        <Button variant="contained" startIcon={<Add />} onClick={handleOpenAdd}>
                            Add Template
                        </Button>
                    )}
                </Paper>
            ) : (
                <Grid container spacing={3}>
                    {templates.map(tpl => (
                        <Grid item xs={12} md={6} lg={4} key={tpl.id}>
                            <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column', border: tpl.is_default ? '2px solid #25D366' : '1px solid #e0e0e0' }}>
                                <CardContent sx={{ flex: 1 }}>
                                    {/* Title row */}
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                                        <Box>
                                            <Typography variant="subtitle1" fontWeight={700}>
                                                {tpl.display_name || tpl.template_name}
                                                {tpl.is_default && (
                                                    <Star sx={{ fontSize: 16, color: '#25D366', ml: 0.5, verticalAlign: 'middle' }} />
                                                )}
                                            </Typography>
                                            <Typography variant="caption" color="text.secondary" sx={{ fontFamily: 'monospace' }}>
                                                {tpl.template_name}
                                            </Typography>
                                        </Box>
                                        <Chip
                                            label={tpl.status}
                                            size="small"
                                            color={tpl.status === 'active' ? 'success' : tpl.status === 'draft' ? 'warning' : 'default'}
                                        />
                                    </Box>

                                    {/* Badges row */}
                                    <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mb: 1.5 }}>
                                        <Chip label={tpl.category} size="small" variant="outlined" />
                                        <Chip label={tpl.language_code} size="small" variant="outlined" />
                                        {tpl.purpose !== 'custom' && <Chip label={tpl.purpose} size="small" color="primary" variant="outlined" />}
                                    </Box>

                                    {/* Body preview */}
                                    <Paper variant="outlined" sx={{ p: 1.5, bgcolor: '#f0fdf4', borderRadius: 2, mb: 1 }}>
                                        <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', fontSize: '0.8rem' }}>
                                            {previewBody(tpl).length > 160
                                                ? previewBody(tpl).substring(0, 157) + '…'
                                                : previewBody(tpl)}
                                        </Typography>
                                        {tpl.footer_text && (
                                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                                                {tpl.footer_text}
                                            </Typography>
                                        )}
                                    </Paper>

                                    {/* Template ID */}
                                    <Typography variant="caption" color="text.disabled">
                                        Meta ID: {tpl.template_id}
                                    </Typography>
                                    {isSuperAdmin && tpl.company_name && (
                                        <Typography variant="caption" color="text.disabled" sx={{ display: 'block' }}>
                                            Company: {tpl.company_name}
                                        </Typography>
                                    )}
                                </CardContent>

                                <CardActions sx={{ justifyContent: 'space-between', pt: 0, px: 2, pb: 1.5 }}>
                                    <Box>
                                        <Tooltip title="Send Template">
                                            <span>
                                                <IconButton
                                                    size="small" color="success"
                                                    disabled={tpl.status !== 'active'}
                                                    onClick={() => handleOpenSend(tpl)}
                                                >
                                                    <Send fontSize="small" />
                                                </IconButton>
                                            </span>
                                        </Tooltip>
                                        <Tooltip title="Bulk Send (Excel)">
                                            <span>
                                                <IconButton
                                                    size="small" color="primary"
                                                    disabled={tpl.status !== 'active'}
                                                    onClick={() => handleOpenBulkSend(tpl)}
                                                >
                                                    <People fontSize="small" />
                                                </IconButton>
                                            </span>
                                        </Tooltip>
                                        <Tooltip title="Send History">
                                            <IconButton size="small" color="info" onClick={() => handleOpenHistory(tpl)}>
                                                <History fontSize="small" />
                                            </IconButton>
                                        </Tooltip>
                                        <Tooltip title="Preview">
                                            <IconButton size="small" onClick={() => { setPreviewTemplate(tpl); setOpenPreview(true); }}>
                                                <Visibility fontSize="small" />
                                            </IconButton>
                                        </Tooltip>
                                    </Box>
                                    <Box>
                                        {(isSuperAdmin || isCompanyAdmin) && (
                                            <>
                                                <Tooltip title="Edit">
                                                    <IconButton size="small" onClick={() => handleOpenEdit(tpl)}>
                                                        <Edit fontSize="small" />
                                                    </IconButton>
                                                </Tooltip>
                                                <Tooltip title="Delete">
                                                    <IconButton size="small" color="error" onClick={() => setDeleteTarget(tpl)}>
                                                        <Delete fontSize="small" />
                                                    </IconButton>
                                                </Tooltip>
                                            </>
                                        )}
                                    </Box>
                                </CardActions>
                            </Card>
                        </Grid>
                    ))}
                </Grid>
            )}

            {/* ── Add / Edit Dialog ─────────────────────────────────────────── */}
            <Dialog open={openDialog} onClose={() => setOpenDialog(false)} maxWidth="md" fullWidth>
                <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    {editingTemplate ? 'Edit Template' : 'Add New Template'}
                    <IconButton onClick={() => setOpenDialog(false)}><Close /></IconButton>
                </DialogTitle>
                <Divider />
                <DialogContent sx={{ p: 0 }}>
                    <Tabs value={dialogTab} onChange={(_, v) => setDialogTab(v)} sx={{ px: 3, borderBottom: 1, borderColor: 'divider' }}>
                        <Tab label="Details" />
                        <Tab label="Content" />
                        <Tab label="Variables" disabled={extractVarIndices(formData.body_text).length === 0} />
                        <Tab label="Buttons" />
                        <Tab label="Preview" />
                    </Tabs>

                    <Box sx={{ px: 3, pb: 2 }}>
                        {/* ── Tab 0: Details ──────────────────────────────── */}
                        <TabPanel value={dialogTab} index={0}>
                            <Grid container spacing={2}>
                                {isSuperAdmin && (
                                    <Grid item xs={12}>
                                        <FormControl fullWidth size="small">
                                            <InputLabel>Company</InputLabel>
                                            <Select value={formData.company_id} onChange={e => handleFormChange('company_id', e.target.value)} label="Company">
                                                {companies.map(c => <MenuItem key={c.id} value={c.id}>{c.company_name}</MenuItem>)}
                                            </Select>
                                        </FormControl>
                                    </Grid>
                                )}
                                <Grid item xs={12} sm={6}>
                                    <TextField fullWidth size="small" label="Meta Template ID *" value={formData.template_id}
                                        onChange={e => handleFormChange('template_id', e.target.value)}
                                        helperText="Numeric ID from Meta Business Manager" />
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <TextField fullWidth size="small" label="Template Name (Meta) *" value={formData.template_name}
                                        onChange={e => handleFormChange('template_name', e.target.value.toLowerCase().replace(/\s+/g, '_'))}
                                        helperText="Exact name on Meta (lowercase, underscores)" />
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <TextField fullWidth size="small" label="Display Name" value={formData.display_name}
                                        onChange={e => handleFormChange('display_name', e.target.value)}
                                        helperText="Friendly name shown in this panel" />
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <TextField fullWidth size="small" label="Language Code" value={formData.language_code}
                                        onChange={e => handleFormChange('language_code', e.target.value)}
                                        helperText="e.g. en, ur, en_US" />
                                </Grid>
                                <Grid item xs={12} sm={4}>
                                    <FormControl fullWidth size="small">
                                        <InputLabel>Category</InputLabel>
                                        <Select value={formData.category} onChange={e => handleFormChange('category', e.target.value)} label="Category">
                                            {CATEGORIES.map(c => <MenuItem key={c} value={c}>{c}</MenuItem>)}
                                        </Select>
                                    </FormControl>
                                </Grid>
                                <Grid item xs={12} sm={4}>
                                    <FormControl fullWidth size="small">
                                        <InputLabel>Purpose</InputLabel>
                                        <Select value={formData.purpose} onChange={e => handleFormChange('purpose', e.target.value)} label="Purpose">
                                            {PURPOSES.map(p => <MenuItem key={p} value={p}>{p}</MenuItem>)}
                                        </Select>
                                    </FormControl>
                                </Grid>
                                <Grid item xs={12} sm={4}>
                                    <FormControl fullWidth size="small">
                                        <InputLabel>Status</InputLabel>
                                        <Select value={formData.status} onChange={e => handleFormChange('status', e.target.value)} label="Status">
                                            {STATUSES.map(s => <MenuItem key={s} value={s}>{s}</MenuItem>)}
                                        </Select>
                                    </FormControl>
                                </Grid>
                                <Grid item xs={12}>
                                    <FormControlLabel
                                        control={<Switch checked={formData.is_default} onChange={e => handleFormChange('is_default', e.target.checked)} color="success" />}
                                        label="Set as default template for this company"
                                    />
                                </Grid>
                                <Grid item xs={12}>
                                    <TextField fullWidth size="small" multiline rows={2} label="Internal Notes" value={formData.notes}
                                        onChange={e => handleFormChange('notes', e.target.value)} />
                                </Grid>
                            </Grid>
                        </TabPanel>

                        {/* ── Tab 1: Content ──────────────────────────────── */}
                        <TabPanel value={dialogTab} index={1}>
                            <Grid container spacing={2}>
                                {/* Header */}
                                <Grid item xs={12}>
                                    <Typography variant="subtitle2" fontWeight={600} gutterBottom>Header (Optional)</Typography>
                                </Grid>
                                <Grid item xs={12} sm={4}>
                                    <FormControl fullWidth size="small">
                                        <InputLabel>Header Type</InputLabel>
                                        <Select value={formData.header_type} onChange={e => handleFormChange('header_type', e.target.value)} label="Header Type">
                                            {HEADER_TYPES.map(t => <MenuItem key={t} value={t}>{t}</MenuItem>)}
                                        </Select>
                                    </FormControl>
                                </Grid>
                                {formData.header_type === 'TEXT' && (
                                    <Grid item xs={12} sm={8}>
                                        <TextField fullWidth size="small" label="Header Text" value={formData.header_text}
                                            onChange={e => handleFormChange('header_text', e.target.value)}
                                            helperText="Use {{1}} for dynamic variable" />
                                    </Grid>
                                )}
                                {['IMAGE', 'VIDEO', 'DOCUMENT'].includes(formData.header_type) && (
                                    <Grid item xs={12} sm={8}>
                                        <Alert severity="info" sx={{ py: 0.5 }}>
                                            When sending, you can provide a media URL for {formData.header_type.toLowerCase()} headers.
                                        </Alert>
                                    </Grid>
                                )}

                                <Grid item xs={12}><Divider /></Grid>

                                {/* Body */}
                                <Grid item xs={12}>
                                    <Typography variant="subtitle2" fontWeight={600} gutterBottom>
                                        Body Text *
                                        <Typography component="span" variant="caption" fontWeight={400} color="text.secondary" sx={{ ml: 1 }}>
                                            Use {'{{1}}'}, {'{{2}}'} … for dynamic variables
                                        </Typography>
                                    </Typography>
                                    <TextField fullWidth multiline rows={5} size="small"
                                        value={formData.body_text}
                                        onChange={e => handleFormChange('body_text', e.target.value)}
                                        placeholder="Hello {{1}}, your order *{{2}}* is ready. Expected delivery: {{3}}."
                                    />
                                    {formData.body_variables.length > 0 && (
                                        <Alert severity="info" sx={{ mt: 1, py: 0.5 }}>
                                            {formData.body_variables.length} variable(s) detected — configure labels in the Variables tab.
                                        </Alert>
                                    )}
                                </Grid>

                                <Grid item xs={12}><Divider /></Grid>

                                {/* Footer */}
                                <Grid item xs={12}>
                                    <Typography variant="subtitle2" fontWeight={600} gutterBottom>Footer (Optional)</Typography>
                                    <TextField fullWidth size="small" label="Footer Text"
                                        value={formData.footer_text}
                                        onChange={e => handleFormChange('footer_text', e.target.value)}
                                        inputProps={{ maxLength: 60 }}
                                        helperText={`${formData.footer_text.length}/60 chars`}
                                    />
                                </Grid>
                            </Grid>
                        </TabPanel>

                        {/* ── Tab 2: Variables ────────────────────────────── */}
                        <TabPanel value={dialogTab} index={2}>
                            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                                Label each variable so you know what to fill in when sending the template.
                            </Typography>
                            {formData.body_variables.length === 0 ? (
                                <Alert severity="warning">No variables detected in body text. Add {'{{1}}'} placeholders in the Content tab.</Alert>
                            ) : (
                                <Grid container spacing={2}>
                                    {formData.body_variables.map(v => (
                                        <Grid item xs={12} key={v.index}>
                                            <Paper variant="outlined" sx={{ p: 2 }}>
                                                <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1 }}>
                                                    {'{{' + v.index + '}}'} — Variable {v.index}
                                                </Typography>
                                                <Grid container spacing={1.5}>
                                                    <Grid item xs={12} sm={4}>
                                                        <TextField fullWidth size="small" label="Internal Name"
                                                            value={v.name}
                                                            onChange={e => handleBodyVarChange(v.index, 'name', e.target.value)}
                                                            placeholder="customer_name"
                                                            helperText="Snake_case key for reference" />
                                                    </Grid>
                                                    <Grid item xs={12} sm={4}>
                                                        <TextField fullWidth size="small" label="Display Label"
                                                            value={v.label}
                                                            onChange={e => handleBodyVarChange(v.index, 'label', e.target.value)}
                                                            placeholder="Customer Name" />
                                                    </Grid>
                                                    <Grid item xs={12} sm={4}>
                                                        <TextField fullWidth size="small" label="Example Value"
                                                            value={v.example}
                                                            onChange={e => handleBodyVarChange(v.index, 'example', e.target.value)}
                                                            placeholder="Ahmad Ali"
                                                            helperText="Used in preview" />
                                                    </Grid>
                                                </Grid>
                                            </Paper>
                                        </Grid>
                                    ))}
                                </Grid>
                            )}
                        </TabPanel>

                        {/* ── Tab 3: Buttons ──────────────────────────────── */}
                        <TabPanel value={dialogTab} index={3}>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                                <Typography variant="body2" color="text.secondary">
                                    Add up to 3 buttons (Quick Reply or Call-to-Action).
                                </Typography>
                                <Button size="small" variant="outlined" startIcon={<Add />}
                                    onClick={addButton} disabled={formData.buttons.length >= 3}>
                                    Add Button
                                </Button>
                            </Box>
                            {formData.buttons.length === 0 ? (
                                <Alert severity="info">No buttons added. Buttons are optional.</Alert>
                            ) : (
                                formData.buttons.map((btn, idx) => (
                                    <Paper variant="outlined" key={idx} sx={{ p: 2, mb: 1.5 }}>
                                        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                                            <Typography variant="subtitle2">Button {idx + 1}</Typography>
                                            <IconButton size="small" color="error" onClick={() => removeButton(idx)}>
                                                <Delete fontSize="small" />
                                            </IconButton>
                                        </Box>
                                        <Grid container spacing={1.5}>
                                            <Grid item xs={12} sm={4}>
                                                <FormControl fullWidth size="small">
                                                    <InputLabel>Type</InputLabel>
                                                    <Select value={btn.type} onChange={e => updateButton(idx, 'type', e.target.value)} label="Type">
                                                        <MenuItem value="QUICK_REPLY">Quick Reply</MenuItem>
                                                        <MenuItem value="URL">URL</MenuItem>
                                                        <MenuItem value="PHONE_NUMBER">Phone Number</MenuItem>
                                                    </Select>
                                                </FormControl>
                                            </Grid>
                                            <Grid item xs={12} sm={4}>
                                                <TextField fullWidth size="small" label="Button Text"
                                                    value={btn.text}
                                                    onChange={e => updateButton(idx, 'text', e.target.value)}
                                                    inputProps={{ maxLength: 25 }}
                                                />
                                            </Grid>
                                            {btn.type === 'URL' && (
                                                <Grid item xs={12} sm={4}>
                                                    <TextField fullWidth size="small" label="URL"
                                                        value={btn.url || ''}
                                                        onChange={e => updateButton(idx, 'url', e.target.value)}
                                                        placeholder="https://..." />
                                                </Grid>
                                            )}
                                            {btn.type === 'PHONE_NUMBER' && (
                                                <Grid item xs={12} sm={4}>
                                                    <TextField fullWidth size="small" label="Phone Number"
                                                        value={btn.phone_number || ''}
                                                        onChange={e => updateButton(idx, 'phone_number', e.target.value)}
                                                        placeholder="+923001234567" />
                                                </Grid>
                                            )}
                                        </Grid>
                                    </Paper>
                                ))
                            )}
                        </TabPanel>

                        {/* ── Tab 4: Preview ──────────────────────────────── */}
                        <TabPanel value={dialogTab} index={4}>
                            <TemplatePreviewCard
                                headerType={formData.header_type}
                                headerText={formData.header_text}
                                bodyText={formData.body_text}
                                bodyVariables={formData.body_variables}
                                footerText={formData.footer_text}
                                buttons={formData.buttons}
                            />
                        </TabPanel>
                    </Box>
                </DialogContent>
                <Divider />
                <DialogActions sx={{ px: 3, py: 2 }}>
                    <Button onClick={() => setOpenDialog(false)}>Cancel</Button>
                    <Button variant="contained" onClick={handleSave} disabled={saving}>
                        {saving ? <CircularProgress size={20} /> : (editingTemplate ? 'Update Template' : 'Create Template')}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* ── Send Dialog ───────────────────────────────────────────────── */}
            <Dialog open={openSendDialog} onClose={() => setOpenSendDialog(false)} maxWidth="sm" fullWidth>
                <DialogTitle>
                    Send Template — {sendTarget?.display_name || sendTarget?.template_name}
                </DialogTitle>
                <Divider />
                <DialogContent>
                    <Grid container spacing={2} sx={{ mt: 0.5 }}>
                        <Grid item xs={12} sm={6}>
                            <TextField fullWidth size="small" label="Recipient Phone *"
                                value={sendForm.recipient_phone}
                                onChange={e => setSendForm(f => ({ ...f, recipient_phone: e.target.value }))}
                                placeholder="+923001234567"
                                helperText="Include country code (no spaces)" />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField fullWidth size="small" label="Recipient Name"
                                value={sendForm.recipient_name}
                                onChange={e => setSendForm(f => ({ ...f, recipient_name: e.target.value }))}
                                placeholder="Optional — for your records" />
                        </Grid>
                        {waNumbers.length > 1 && (
                            <Grid item xs={12}>
                                <FormControl fullWidth size="small">
                                    <InputLabel>Send From (WhatsApp Number)</InputLabel>
                                    <Select value={sendForm.whatsapp_number_id}
                                        onChange={e => setSendForm(f => ({ ...f, whatsapp_number_id: e.target.value }))}
                                        label="Send From (WhatsApp Number)">
                                        {waNumbers.map(n => <MenuItem key={n.id} value={n.id}>{n.display_name} — {n.phone_number}</MenuItem>)}
                                    </Select>
                                </FormControl>
                            </Grid>
                        )}

                        {/* Header media URL (for IMAGE, VIDEO, DOCUMENT headers) */}
                        {sendTarget && ['IMAGE', 'VIDEO', 'DOCUMENT'].includes(sendTarget.header_type) && (
                            <>
                                <Grid item xs={12}>
                                    <Typography variant="subtitle2" fontWeight={600} gutterBottom>
                                        {sendTarget.header_type} for Template
                                    </Typography>
                                </Grid>
                                <Grid item xs={12} sm={8}>
                                    <TextField fullWidth size="small"
                                        label={`${sendTarget.header_type} URL *`}
                                        value={sendForm.header_variables?.url || ''}
                                        onChange={e => setSendForm(f => ({
                                            ...f,
                                            header_variables: { ...f.header_variables, url: e.target.value }
                                        }))}
                                        placeholder="https://example.com/image.jpg"
                                        helperText="Paste URL or upload file below"
                                        disabled={uploadingMedia}
                                    />
                                </Grid>
                                <Grid item xs={12} sm={4}>
                                    <Button
                                        variant="outlined"
                                        component="label"
                                        fullWidth
                                        disabled={uploadingMedia}
                                        sx={{ height: '40px' }}
                                        startIcon={uploadingMedia ? <CircularProgress size={16} /> : null}
                                    >
                                        {uploadingMedia ? 'Uploading...' : 'Upload File'}
                                        <input
                                            type="file"
                                            hidden
                                            accept={sendTarget.header_type === 'IMAGE' ? 'image/*' : sendTarget.header_type === 'VIDEO' ? 'video/*' : '*/*'}
                                            onChange={handleMediaUpload}
                                        />
                                    </Button>
                                </Grid>
                            </>
                        )}

                        {/* Body variables */}
                        {Object.keys(sendForm.body_variables).length > 0 && (
                            <>
                                <Grid item xs={12}>
                                    <Typography variant="subtitle2" fontWeight={600}>Fill in Template Variables</Typography>
                                </Grid>
                                {(() => {
                                    const bodyVarDefs = sendTarget?.body_variables
                                        ? (typeof sendTarget.body_variables === 'string' ? JSON.parse(sendTarget.body_variables) : sendTarget.body_variables)
                                        : [];
                                    const defMap = bodyVarDefs.reduce((acc, v) => { acc[v.index] = v; return acc; }, {});
                                    return Object.keys(sendForm.body_variables).map(idx => (
                                        <Grid item xs={12} sm={6} key={idx}>
                                            <TextField fullWidth size="small"
                                                label={defMap[idx]?.label || `Variable {{${idx}}}`}
                                                value={sendForm.body_variables[idx]}
                                                onChange={e => setSendForm(f => ({
                                                    ...f,
                                                    body_variables: { ...f.body_variables, [idx]: e.target.value }
                                                }))}
                                                placeholder={defMap[idx]?.example || ''}
                                            />
                                        </Grid>
                                    ));
                                })()}
                            </>
                        )}

                        {/* Live preview */}
                        {sendTarget && (
                            <Grid item xs={12}>
                                <Divider sx={{ my: 1 }} />
                                <Typography variant="caption" color="text.secondary" fontWeight={600}>PREVIEW</Typography>
                                <TemplatePreviewCard
                                    headerType={sendTarget.header_type}
                                    headerText={applyVars(sendTarget.header_text, sendForm.header_variables)}
                                    bodyText={applyVars(sendTarget.body_text, sendForm.body_variables)}
                                    bodyVariables={[]}
                                    footerText={sendTarget.footer_text}
                                    buttons={sendTarget.buttons ? (typeof sendTarget.buttons === 'string' ? JSON.parse(sendTarget.buttons) : sendTarget.buttons) : []}
                                    compact
                                />
                            </Grid>
                        )}
                    </Grid>
                </DialogContent>
                <Divider />
                <DialogActions sx={{ px: 3, py: 2 }}>
                    <Button onClick={() => setOpenSendDialog(false)}>Cancel</Button>
                    <Button variant="contained" color="success" onClick={handleSend}
                        disabled={
                            sending ||
                            !sendForm.recipient_phone ||
                            (sendTarget && ['IMAGE', 'VIDEO', 'DOCUMENT'].includes(sendTarget.header_type) && !sendForm.header_variables?.url)
                        }>
                        {sending ? <CircularProgress size={20} /> : 'Send Template'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* ── History Dialog ────────────────────────────────────────────── */}
            <Dialog open={openHistory} onClose={() => setOpenHistory(false)} maxWidth="md" fullWidth>
                <DialogTitle>
                    Send History — {historyTarget?.display_name || historyTarget?.template_name}
                </DialogTitle>
                <Divider />
                <DialogContent sx={{ p: 0 }}>
                    {historyLoading ? (
                        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress /></Box>
                    ) : historyData.length === 0 ? (
                        <Box sx={{ py: 6, textAlign: 'center' }}>
                            <Typography color="text.secondary">No sends recorded for this template.</Typography>
                        </Box>
                    ) : (
                        <TableContainer>
                            <Table size="small">
                                <TableHead>
                                    <TableRow sx={{ bgcolor: '#f8f9fa' }}>
                                        <TableCell>Recipient</TableCell>
                                        <TableCell>WhatsApp #</TableCell>
                                        <TableCell>Status</TableCell>
                                        <TableCell>Sent At</TableCell>
                                        <TableCell>Meta Msg ID</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {historyData.map(row => (
                                        <TableRow key={row.id} hover>
                                            <TableCell>
                                                <Typography variant="body2">{row.recipient_phone}</Typography>
                                                {row.recipient_name && <Typography variant="caption" color="text.secondary">{row.recipient_name}</Typography>}
                                            </TableCell>
                                            <TableCell>
                                                <Typography variant="body2">{row.whatsapp_number_name || '—'}</Typography>
                                            </TableCell>
                                            <TableCell>
                                                <Chip
                                                    label={row.status}
                                                    size="small"
                                                    color={
                                                        row.status === 'delivered' || row.status === 'read' ? 'success'
                                                            : row.status === 'sent' ? 'info'
                                                                : row.status === 'failed' ? 'error'
                                                                    : 'default'
                                                    }
                                                />
                                                {row.error_message && (
                                                    <Tooltip title={row.error_message}>
                                                        <Typography variant="caption" color="error" sx={{ display: 'block', cursor: 'help' }}>
                                                            {row.error_message.substring(0, 40)}…
                                                        </Typography>
                                                    </Tooltip>
                                                )}
                                            </TableCell>
                                            <TableCell>
                                                <Typography variant="body2">
                                                    {new Date(row.sent_at).toLocaleString()}
                                                </Typography>
                                            </TableCell>
                                            <TableCell>
                                                <Typography variant="caption" sx={{ fontFamily: 'monospace' }}>
                                                    {row.message_id ? row.message_id.substring(0, 20) + '…' : '—'}
                                                </Typography>
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    )}
                </DialogContent>
                <DialogActions sx={{ px: 3, py: 2 }}>
                    <Button onClick={() => setOpenHistory(false)}>Close</Button>
                </DialogActions>
            </Dialog>

            {/* ── Template Preview Dialog ───────────────────────────────────── */}
            <Dialog open={openPreview} onClose={() => setOpenPreview(false)} maxWidth="sm" fullWidth>
                <DialogTitle>
                    Template Preview — {previewTemplate?.display_name || previewTemplate?.template_name}
                    <IconButton onClick={() => setOpenPreview(false)} sx={{ position: 'absolute', right: 12, top: 12 }}><Close /></IconButton>
                </DialogTitle>
                <DialogContent>
                    {previewTemplate && (
                        <TemplatePreviewCard
                            headerType={previewTemplate.header_type}
                            headerText={previewTemplate.header_text}
                            bodyText={previewBody(previewTemplate)}
                            bodyVariables={[]}
                            footerText={previewTemplate.footer_text}
                            buttons={previewTemplate.buttons
                                ? (typeof previewTemplate.buttons === 'string' ? JSON.parse(previewTemplate.buttons) : previewTemplate.buttons)
                                : []}
                        />
                    )}
                </DialogContent>
            </Dialog>

            {/* ── Delete Confirm Dialog ─────────────────────────────────────── */}
            <Dialog open={Boolean(deleteTarget)} onClose={() => setDeleteTarget(null)} maxWidth="xs" fullWidth>
                <DialogTitle>Delete Template</DialogTitle>
                <DialogContent>
                    <Alert severity="error">
                        Are you sure you want to delete <strong>{deleteTarget?.display_name || deleteTarget?.template_name}</strong>?
                        This will also remove all send history for this template.
                    </Alert>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDeleteTarget(null)}>Cancel</Button>
                    <Button color="error" variant="contained" onClick={handleDelete}>Delete</Button>
                </DialogActions>
            </Dialog>

            {/* ── Bulk Send Dialog ──────────────────────────────────────────── */}
            <Dialog open={openBulkDialog} onClose={() => setOpenBulkDialog(false)} maxWidth="md" fullWidth>
                <DialogTitle>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <People color="primary" />
                        Bulk Send — {bulkTarget?.display_name || bulkTarget?.template_name}
                    </Box>
                </DialogTitle>
                <Divider />
                <DialogContent>
                    {!bulkResults ? (
                        <Grid container spacing={2} sx={{ mt: 0.5 }}>
                            <Grid item xs={12}>
                                <Alert severity="info" sx={{ mb: 2 }}>
                                    <Typography variant="body2" fontWeight={600} gutterBottom>
                                        Upload an Excel file (.xlsx, .xls, .csv) with recipient data
                                    </Typography>
                                    <Typography variant="caption" component="div">
                                        • Required column: <strong>phone</strong> (with country code, e.g., +923001234567)
                                    </Typography>
                                    <Typography variant="caption" component="div">
                                        • Optional column: <strong>name</strong> (recipient name)
                                    </Typography>
                                    {bulkTarget?.body_variables && (() => {
                                        const vars = typeof bulkTarget.body_variables === 'string' 
                                            ? JSON.parse(bulkTarget.body_variables) 
                                            : bulkTarget.body_variables;
                                        return vars.map(v => (
                                            <Typography variant="caption" component="div" key={v.index}>
                                                • Template variable: <strong>{v.name || `var${v.index}`}</strong> ({v.label})
                                            </Typography>
                                        ));
                                    })()}
                                </Alert>
                            </Grid>

                            <Grid item xs={12}>
                                <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                                    <Button
                                        variant="outlined"
                                        component="label"
                                        startIcon={<CloudUpload />}
                                        fullWidth
                                    >
                                        {bulkFile ? bulkFile.name : 'Choose Excel File'}
                                        <input
                                            type="file"
                                            hidden
                                            accept=".xlsx,.xls,.csv"
                                            onChange={handleBulkFileChange}
                                        />
                                    </Button>
                                    <Button
                                        variant="text"
                                        startIcon={<GetApp />}
                                        onClick={downloadSampleExcel}
                                    >
                                        Download Sample
                                    </Button>
                                </Box>
                                {bulkFile && (
                                    <Typography variant="caption" color="success.main" sx={{ display: 'block', mt: 1 }}>
                                        ✓ File selected: {bulkFile.name} ({(bulkFile.size / 1024).toFixed(2)} KB)
                                    </Typography>
                                )}
                            </Grid>

                            {waNumbers.length > 1 && (
                                <Grid item xs={12}>
                                    <FormControl fullWidth size="small">
                                        <InputLabel>Send From (WhatsApp Number)</InputLabel>
                                        <Select defaultValue={waNumbers[0]?.id || ''} label="Send From (WhatsApp Number)">
                                            {waNumbers.map(n => (
                                                <MenuItem key={n.id} value={n.id}>
                                                    {n.display_name} — {n.phone_number}
                                                </MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                </Grid>
                            )}
                        </Grid>
                    ) : (
                        <Box>
                            <Alert severity={bulkResults.failed === 0 ? 'success' : 'warning'} sx={{ mb: 2 }}>
                                <Typography variant="body2" fontWeight={600}>
                                    Bulk Send Complete
                                </Typography>
                                <Typography variant="body2">
                                    Total: {bulkResults.total} | Success: {bulkResults.success} | Failed: {bulkResults.failed}
                                </Typography>
                            </Alert>

                            {bulkResults.errors && bulkResults.errors.length > 0 && (
                                <Box>
                                    <Typography variant="subtitle2" color="error" gutterBottom>
                                        Errors ({bulkResults.errors.length}):
                                    </Typography>
                                    <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 300 }}>
                                        <Table size="small">
                                            <TableHead>
                                                <TableRow>
                                                    <TableCell>Row</TableCell>
                                                    <TableCell>Phone</TableCell>
                                                    <TableCell>Error</TableCell>
                                                </TableRow>
                                            </TableHead>
                                            <TableBody>
                                                {bulkResults.errors.map((err, idx) => (
                                                    <TableRow key={idx}>
                                                        <TableCell>{err.row}</TableCell>
                                                        <TableCell>{err.phone || '—'}</TableCell>
                                                        <TableCell>
                                                            <Typography variant="caption" color="error">
                                                                {err.error}
                                                            </Typography>
                                                        </TableCell>
                                                    </TableRow>
                                                ))}
                                            </TableBody>
                                        </Table>
                                    </TableContainer>
                                </Box>
                            )}
                        </Box>
                    )}
                </DialogContent>
                <Divider />
                <DialogActions sx={{ px: 3, py: 2 }}>
                    <Button onClick={() => setOpenBulkDialog(false)}>Close</Button>
                    {!bulkResults && (
                        <Button
                            variant="contained"
                            color="primary"
                            onClick={handleBulkSend}
                            disabled={bulkSending || !bulkFile}
                            startIcon={bulkSending ? <CircularProgress size={20} /> : <People />}
                        >
                            {bulkSending ? 'Sending...' : 'Send to All'}
                        </Button>
                    )}
                </DialogActions>
            </Dialog>
        </Box>
    );
};

// ──── Template Preview Card ───────────────────────────────────────────────────
const TemplatePreviewCard = ({ headerType, headerText, bodyText, bodyVariables, footerText, buttons, compact }) => {
    const exampleVars = (bodyVariables || []).reduce((acc, v) => {
        acc[v.index] = v.example || `[${v.label || v.index}]`;
        return acc;
    }, {});
    const resolvedBody = applyVars(bodyText, exampleVars);

    return (
        <Box sx={{
            maxWidth: 380, mx: 'auto', mt: 1,
            '& .bubble': {
                bgcolor: '#dcf8c6',
                borderRadius: '12px 12px 4px 12px',
                px: 2, py: 1.5,
                boxShadow: '0 1px 2px rgba(0,0,0,0.15)',
            }
        }}>
            <Box sx={{ bgcolor: '#e5ddd5', borderRadius: 3, p: 1.5, overflowX: 'hidden' }}>
                {/* Header */}
                {headerType && headerType !== 'NONE' && (
                    <Box className="bubble" sx={{ mb: 0.5, borderRadius: '12px 12px 4px 4px !important' }}>
                        {headerType === 'TEXT' && (
                            <Typography variant="body2" fontWeight={700}>{headerText || 'Header Text'}</Typography>
                        )}
                        {headerType === 'IMAGE' && (
                            <Box sx={{ bgcolor: '#ccc', height: 100, display: 'flex', alignItems: 'center', justifyContent: 'center', borderRadius: 1 }}>
                                <Typography variant="caption" color="text.secondary">Image Header</Typography>
                            </Box>
                        )}
                        {['VIDEO', 'DOCUMENT'].includes(headerType) && (
                            <Box sx={{ bgcolor: '#ccc', height: 50, display: 'flex', alignItems: 'center', justifyContent: 'center', borderRadius: 1 }}>
                                <Typography variant="caption" color="text.secondary">{headerType}</Typography>
                            </Box>
                        )}
                    </Box>
                )}

                {/* Body */}
                <Box className="bubble" sx={{
                    borderRadius: headerType && headerType !== 'NONE'
                        ? '4px 4px 4px 12px !important'
                        : '12px 12px 4px 12px !important'
                }}>
                    <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', fontSize: '0.82rem' }}>
                        {resolvedBody || 'Template body text will appear here…'}
                    </Typography>
                    {footerText && (
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.75 }}>
                            {footerText}
                        </Typography>
                    )}
                    <Typography variant="caption" color="text.disabled" sx={{ display: 'block', textAlign: 'right', mt: 0.5, fontSize: '0.65rem' }}>
                        {new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })} ✓✓
                    </Typography>
                </Box>

                {/* Buttons */}
                {buttons && buttons.length > 0 && (
                    <Box sx={{ mt: 0.5, display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                        {buttons.map((btn, i) => (
                            <Box key={i} sx={{
                                bgcolor: 'white', borderRadius: 2, py: 1, textAlign: 'center',
                                color: '#0077cc', fontWeight: 600, fontSize: '0.82rem', cursor: 'default',
                                boxShadow: '0 1px 2px rgba(0,0,0,0.1)'
                            }}>
                                {btn.text || 'Button'}
                            </Box>
                        ))}
                    </Box>
                )}
            </Box>
        </Box>
    );
};

export default WhatsAppTemplates;
